import {
  NUTRITION_MISSING_REMOVAL_MESSAGE,
  NUTRITION_SEASON_NO_PLAN_MESSAGE,
} from 'constants/messageConstants';
import {
  NUTRITION_COLUMN_PRODUCT,
  NUTRITION_COLUMN_DATE,
  NUTRITION_COLUMN_RATE,
  NUTRITION_COLUMN_SEASON,
  NUTRITION_ROW_APPLIED,
  NUTRITION_ROW_BALANCE,
  NUTRITION_ROW_CROP_REMOVAL,
  NUTRITION_ROW_REMOVED,
  NUTRITION_ROW_TOTAL_APPLIED,
  NUTRITION_ROW_TOTAL_BALANCE,
  NUTRITION_ROW_TOTAL_REMOVED,
  NUTRITION_UNIT_KG_PER_HA,
  NUTRITION_SEASON_NO_PRODUCTS_LABEL,
  NUTRITION_PADDOCK_YEAR_LABEL,
} from 'constants/uiConstants';
import { NUTRITION_ADD_REMOVE_PRODUCTS_ENABLED } from 'constants/appConstants';
import React from 'react';
import { LuAlertTriangle } from 'react-icons/lu';
import {
  NUTRIENT_SHORT_LABELS,
  type NutrientKey,
  type NutritionTally,
} from '../computeLiveTotals';
import type {
  NutritionSeason,
  PaddockNutritionAudit,
} from './computeNutritionAudit';
import IconTip from './IconTip';
import NutrientBalanceValue from './NutrientBalanceValue';
import { formatKgPerHa, formatPlanSummary } from './nutritionFormat';
import ProductName from './ProductName';
import ProductDate from './ProductDate';
import ProductRate from './ProductRate';
import type { NutritionProgramEdit } from './useNutritionProgramEdit';
import AddProductControl from './AddProductControl';
import ProductRowMenu from './ProductRowMenu';

interface PaddockNutritionTableProps {
  paddock: PaddockNutritionAudit;
  nutrients: NutrientKey[];
  /** Every season in range, oldest first — a season with no plan gets a row saying so. */
  seasons: number[];
  edit: NutritionProgramEdit;
  /** Which yield the year headings show, e.g. "Expected yield". */
  yieldLabel: string;
}

const VALUE_CELL = 'px-3 py-1.5 text-right tabular-nums';
const LABEL_CELL = 'px-3 py-1.5 text-left font-semibold text-charcoal';
// The season's applied / removed / balance rows sit on a grey band under its
// products, so the totals read apart from the product lines above them.
const SUBTOTAL_ROW = 'bg-secondary-light';

const AmountCells: React.FC<{
  nutrients: NutrientKey[];
  values: Partial<NutritionTally>;
}> = ({ nutrients, values }) => (
  <>
    {nutrients.map((key) => {
      const value = values[key] ?? 0;
      return (
        <td key={key} className={`${VALUE_CELL} text-charcoal`}>
          {value > 0 ? (
            formatKgPerHa(value)
          ) : (
            <span className="text-gray-muted">—</span>
          )}
        </td>
      );
    })}
  </>
);

const BalanceCells: React.FC<{
  nutrients: NutrientKey[];
  applied: NutritionTally;
  removed: NutritionTally;
  balance: NutritionTally;
}> = ({ nutrients, applied, removed, balance }) => (
  <>
    {nutrients.map((key) => (
      <td key={key} className={VALUE_CELL}>
        <NutrientBalanceValue
          nutrient={key}
          applied={applied[key]}
          removed={removed[key]}
          balance={balance[key]}
        />
      </td>
    ))}
  </>
);

const NoPlanRow: React.FC<{ season: number; colSpan: number }> = ({
  season,
  colSpan,
}) => (
  <tr className="border-t border-gray-bright">
    <td className="px-2 pb-2 pt-3 text-left font-semibold text-charcoal">
      {season}
    </td>
    <td
      colSpan={colSpan - 1}
      className="px-3 pb-2 pt-3 text-left text-gray-muted"
    >
      {NUTRITION_SEASON_NO_PLAN_MESSAGE}
    </td>
  </tr>
);

const SeasonRows: React.FC<{
  paddockName: string;
  season: NutritionSeason;
  nutrients: NutrientKey[];
  edit: NutritionProgramEdit;
  yieldLabel: string;
}> = ({ paddockName, season, nutrients, edit, yieldLabel }) => {
  const location = NUTRITION_PADDOCK_YEAR_LABEL(paddockName, season.season);
  const hasProducts = season.plans.some((plan) => plan.products.length > 0);
  const hasRemoval = nutrients.some((key) => season.removed[key] > 0);
  // A year with no fertiliser keeps Removed and Balance only when the crop
  // still took nutrients off, so the decline shows.
  const showTotals = hasProducts || hasRemoval;
  // With one plan its crop removal is the year's Removed row; with more,
  // each plan shows its own so the year's total has visible parts.
  const showPlanRemoval = season.plans.length > 1;

  return (
    <>
      <tr className="border-t border-gray-bright">
        <td
          colSpan={4 + nutrients.length}
          className="px-2 pb-1 pt-3 text-left font-bold text-charcoal"
        >
          {season.season}
        </td>
      </tr>
      {season.plans.map((plan) => (
        <React.Fragment key={plan.inputId}>
          <tr>
            <td />
            <td
              colSpan={3 + nutrients.length}
              className="px-3 pb-1 pt-2 text-left text-sm font-semibold text-gray-dark"
            >
              <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1.5">
                  {formatPlanSummary(plan, yieldLabel)}
                  {plan.removalMissing && (
                    <IconTip
                      title={NUTRITION_MISSING_REMOVAL_MESSAGE([season.season])}
                    >
                      <LuAlertTriangle size={14} aria-hidden />
                    </IconTip>
                  )}
                </span>
                {edit.canEdit && NUTRITION_ADD_REMOVE_PRODUCTS_ENABLED && (
                  <AddProductControl
                    plan={plan}
                    location={location}
                    edit={edit}
                  />
                )}
              </span>
            </td>
          </tr>
          {plan.products.length === 0 && (
            <tr>
              <td />
              <td
                colSpan={3 + nutrients.length}
                className="px-3 py-1.5 text-left text-gray-muted"
              >
                {NUTRITION_SEASON_NO_PRODUCTS_LABEL}
              </td>
            </tr>
          )}
          {plan.products.map((product) => (
            <ProductRowMenu key={product.key} line={product} edit={edit}>
              <tr className="border-b border-gray-bright last:border-b-0 hover:bg-rowHover">
                <td />
                <td className="min-w-[14rem] px-3 py-1.5 text-left text-charcoal">
                  <ProductName line={product} />
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-left text-charcoal">
                  <ProductRate line={product} edit={edit} />
                </td>
                <td className="px-3 py-1.5 text-left text-sm text-charcoal">
                  <ProductDate line={product} />
                </td>
                <AmountCells nutrients={nutrients} values={product.perHa} />
              </tr>
            </ProductRowMenu>
          ))}
          {showPlanRemoval &&
            nutrients.some((key) => plan.removed[key] > 0) && (
              <tr className="border-t border-dashed border-gray-medium">
                <td />
                <td colSpan={3} className={LABEL_CELL}>
                  {NUTRITION_ROW_CROP_REMOVAL}
                </td>
                {nutrients.map((key) => (
                  <td
                    key={key}
                    className={`${VALUE_CELL} font-semibold text-charcoal`}
                  >
                    {plan.removed[key] > 0 ? (
                      `−${formatKgPerHa(plan.removed[key])}`
                    ) : (
                      <span className="text-gray-muted">—</span>
                    )}
                  </td>
                ))}
              </tr>
            )}
        </React.Fragment>
      ))}
      {hasProducts && (
        <tr className={`${SUBTOTAL_ROW} border-t border-gray-medium`}>
          <td />
          <td colSpan={3} className={LABEL_CELL}>
            {NUTRITION_ROW_APPLIED}
          </td>
          <AmountCells nutrients={nutrients} values={season.applied} />
        </tr>
      )}
      {showTotals && (
        <>
          <tr
            className={`${SUBTOTAL_ROW} ${hasProducts ? '' : 'border-t border-gray-medium'}`}
          >
            <td />
            <td colSpan={3} className={LABEL_CELL}>
              {NUTRITION_ROW_REMOVED}
            </td>
            <AmountCells nutrients={nutrients} values={season.removed} />
          </tr>
          <tr
            className={`${SUBTOTAL_ROW} border-t border-gray-medium font-bold`}
          >
            <td />
            <td colSpan={3} className={LABEL_CELL}>
              {NUTRITION_ROW_BALANCE}
            </td>
            <BalanceCells
              nutrients={nutrients}
              applied={season.applied}
              removed={season.removed}
              balance={season.balance}
            />
          </tr>
        </>
      )}
    </>
  );
};

/**
 * Single-paddock nutrient audit (legacy Fertiliser History parity): each
 * season with its crop, the products applied and the nutrients each one
 * supplied, then applied / removed / balance for the season and for the
 * whole range. All figures kg/ha.
 */
const PaddockNutritionTable: React.FC<PaddockNutritionTableProps> = ({
  paddock,
  nutrients,
  seasons,
  edit,
  yieldLabel,
}) => (
  <div className="summary-grid-scroll w-0 min-w-full overflow-x-auto">
    <table className="w-max border-collapse text-base">
      <thead>
        <tr className="border-b border-gray-bright">
          <th className="px-3 pb-2 text-left font-bold text-charcoal">
            {NUTRITION_COLUMN_SEASON}
          </th>
          <th className="px-3 pb-2 text-left font-bold text-charcoal">
            {NUTRITION_COLUMN_PRODUCT}
          </th>
          {/* Left-aligned, so the heading sits over the rate boxes. */}
          <th className="px-3 pb-2 text-left font-bold text-charcoal">
            {NUTRITION_COLUMN_RATE}
          </th>
          <th className="px-3 pb-2 text-left font-bold text-charcoal">
            {NUTRITION_COLUMN_DATE}
          </th>
          {nutrients.map((key) => (
            <th
              key={key}
              className="px-3 pb-2 text-right font-bold text-charcoal"
            >
              {NUTRIENT_SHORT_LABELS[key]}
              <span className="block text-sm font-normal text-gray-muted">
                {NUTRITION_UNIT_KG_PER_HA}
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {seasons.map((year) => {
          const season = paddock.seasons.find((s) => s.season === year);
          return season ? (
            <SeasonRows
              key={year}
              paddockName={paddock.paddockName}
              season={season}
              nutrients={nutrients}
              edit={edit}
              yieldLabel={yieldLabel}
            />
          ) : (
            <NoPlanRow key={year} season={year} colSpan={4 + nutrients.length} />
          );
        })}
      </tbody>
      <tfoot className="border-t-2 border-gray-medium bg-secondary-light">
        <tr>
          <td colSpan={4} className={`${LABEL_CELL} font-semibold`}>
            {NUTRITION_ROW_TOTAL_APPLIED}
          </td>
          <AmountCells nutrients={nutrients} values={paddock.applied} />
        </tr>
        <tr>
          <td colSpan={4} className={`${LABEL_CELL} font-semibold`}>
            {NUTRITION_ROW_TOTAL_REMOVED}
          </td>
          <AmountCells nutrients={nutrients} values={paddock.removed} />
        </tr>
        <tr>
          <td colSpan={4} className={`${LABEL_CELL} font-semibold`}>
            {NUTRITION_ROW_TOTAL_BALANCE}
          </td>
          <BalanceCells
            nutrients={nutrients}
            applied={paddock.applied}
            removed={paddock.removed}
            balance={paddock.balance}
          />
        </tr>
      </tfoot>
    </table>
  </div>
);

export default PaddockNutritionTable;
